import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account, AccountAssignments, Party, RateChangeResponse } from '../api';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageToolbar } from '../ui/PageToolbar';
import { Btn, Card, EmptyState, RowSkeleton, StatePill } from '../ui/components';
import {
  Button,
  ErrorBanner,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { usePinGate } from '../auth/pinGate';
import { EditAccountDialog } from '../accounts/EditAccountDialog';
import { Wallet } from 'lucide-react';

export function AccountsManagePage() {
  const readOnly = useReadOnly();

  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
  });
  const uplinesQ = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });

  const uplineMap = new Map((uplinesQ.data ?? []).map((p) => [p.id, p.displayName]));

  // Group by upline
  const byUpline = new Map<number, Account[]>();
  for (const a of accountsQ.data ?? []) {
    const list = byUpline.get(a.uplinePartyId) ?? [];
    list.push(a);
    byUpline.set(a.uplinePartyId, list);
  }

  return (
    <AppShell
      title="Accounts"
      toolbar={<PageToolbar title="Accounts" />}
    >
      <div className="space-y-6">
        {accountsQ.isLoading && (
          <Card padding={false}>
            <div className="px-4 py-3">
              <RowSkeleton rows={4} />
            </div>
          </Card>
        )}
        {Array.from(byUpline.entries()).map(([uplineId, accounts]) => (
          <div key={uplineId}>
            <h2 className="mb-3 text-caption font-semibold uppercase tracking-wide text-brand">
              {uplineMap.get(uplineId) ?? `Upline #${uplineId}`}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {accounts.map((a) => (
                <AccountCard key={a.id} account={a} readOnly={readOnly} />
              ))}
            </div>
          </div>
        ))}
        {!accountsQ.isLoading && (accountsQ.data?.length ?? 0) === 0 && (
          <EmptyState
            icon={Wallet}
            title="No accounts yet"
            description="Add accounts in the Setup wizard."
            action={
              !readOnly ? (
                <Btn size="sm" onClick={() => window.location.assign('/wizard')}>
                  Go to Setup
                </Btn>
              ) : undefined
            }
          />
        )}
      </div>
    </AppShell>
  );
}

function AccountCard({ account, readOnly }: { account: Account; readOnly: boolean }) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const [editing, setEditing] = useState(false);
  const [editMetaOpen, setEditMetaOpen] = useState(false);

  const assignmentsQ = useQuery<AccountAssignments>({
    queryKey: ['accounts', account.id, 'assignments'],
    queryFn: () => api.get<AccountAssignments>(`/api/accounts/${account.id}/assignments`),
  });

  const active = assignmentsQ.data?.activeAssignment;

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-small font-medium text-ink">
            {account.canonicalName}
          </p>
          {account.panel && (
            <p className="mt-0.5 text-caption text-ink-muted">{account.panel}</p>
          )}
        </div>
        <StatePill state={account.status} />
      </div>

      {assignmentsQ.isLoading ? (
        <p className="mt-3 text-caption text-ink-muted">Loading rates…</p>
      ) : active ? (
        <dl className="mt-3 space-y-1.5 text-small">
          <div className="flex justify-between">
            <dt className="text-ink-muted">Upline rate</dt>
            <dd className="font-mono text-ink">{active.uplineRate}</dd>
          </div>
          {active.clientRate && (
            <div className="flex justify-between">
              <dt className="text-ink-muted">Client rate</dt>
              <dd className="font-mono text-ink">{active.clientRate}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-ink-muted">Active from</dt>
            <dd className="font-mono text-ink-muted">{active.effectiveFrom}</dd>
          </div>
        </dl>
      ) : (
        <p className="mt-3 text-caption font-medium text-warn">No active assignment</p>
      )}

      {account.notes && (
        <p className="mt-2 border-l-2 border-border pl-2 text-caption text-ink-muted">
          {account.notes}
        </p>
      )}

      {!readOnly && !editing && (
        <div className="mt-3 flex gap-2">
          {active && (
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 justify-center"
              onClick={() => setEditing(true)}
            >
              Edit rates
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 justify-center"
            onClick={() => setEditMetaOpen(true)}
          >
            Edit details
          </Button>
        </div>
      )}

      {!readOnly && editing && active && (
        <RateEditForm
          account={account}
          currentUplineRate={active.uplineRate}
          currentClientRate={active.clientRate ?? ''}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void qc.invalidateQueries({ queryKey: ['accounts', account.id, 'assignments'] });
          }}
          withPin={withPin}
        />
      )}

      {editMetaOpen && (
        <EditAccountDialog
          account={account}
          onClose={() => setEditMetaOpen(false)}
          onSaved={() => setEditMetaOpen(false)}
        />
      )}
    </Card>
  );
}

function RateEditForm({
  account,
  currentUplineRate,
  currentClientRate,
  onCancel,
  onSaved,
  withPin,
}: {
  account: Account;
  currentUplineRate: string;
  currentClientRate: string;
  onCancel: () => void;
  onSaved: () => void;
  withPin: ReturnType<typeof usePinGate>['withPin'];
}) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [uplineRate, setUplineRate] = useState(currentUplineRate);
  const [clientRate, setClientRate] = useState(currentClientRate);
  const [equalSpread, setEqualSpread] = useState(currentUplineRate === currentClientRate);
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      api.patch<RateChangeResponse>(`/api/accounts/${account.id}/rate`, {
        uplineRate,
        clientRate: equalSpread ? uplineRate : clientRate || null,
        equalSpread,
        effectiveFrom,
      }),
    onSuccess: () => {
      setError(null);
      setOk(true);
      setTimeout(onSaved, 800);
    },
    onError: (e) => {
      setOk(false);
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  return (
    <form
      className="mt-3 space-y-3 border-t border-border pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        setOk(false);
        void withPin(() => mut.mutateAsync());
      }}
    >
      <TextInput
        label="Upline rate (cost %)"
        value={uplineRate}
        onChange={(e) => {
          setUplineRate(e.target.value);
          if (equalSpread) setClientRate(e.target.value);
        }}
        name="uplineRate"
        hint="e.g. 0.060000"
      />

      <label className="flex cursor-pointer select-none items-center gap-2 text-small text-ink-muted">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-border bg-surface accent-brand"
          checked={equalSpread}
          onChange={(e) => {
            setEqualSpread(e.target.checked);
            if (e.target.checked) setClientRate(uplineRate);
          }}
        />
        Equal spread (client rate = upline rate)
      </label>

      {!equalSpread && (
        <TextInput
          label="Client rate"
          value={clientRate}
          onChange={(e) => setClientRate(e.target.value)}
          name="clientRate"
          hint="Leave blank for self-held accounts"
        />
      )}

      <TextInput
        label="Effective from"
        type="date"
        value={effectiveFrom}
        onChange={(e) => setEffectiveFrom(e.target.value)}
        name="effectiveFrom"
      />

      {error && <ErrorBanner>{error}</ErrorBanner>}
      {ok && <p className="text-caption text-pos">Rate updated.</p>}

      <div className="flex gap-2">
        <Button type="submit" disabled={mut.isPending}>
          {mut.isPending ? <Spinner /> : 'Save rate change'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={mut.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

